import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { handleUnauthorized } from '../../TokenAuth/auth';

const initialState = {
    poojaTag: [],
    status: 'idle',
    loading: false,
    error: null,
    pagination: {
        totalDocs: 0,
        totalPages: 1,
        page: 1,
        limit: 10,
    },
};

export const fetchPoojaTag = createAsyncThunk(
    'poojaTag/fetch',
    async ({ page, limit, search = '' } = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");

            let url = `${process.env.REACT_APP_BASEURL}/admin/pooja/tag`;
            const params = new URLSearchParams();
            if (page !== undefined) params.append('page', page);
            if (limit !== undefined) params.append('limit', limit);
            if (search) params.append('search', search);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            if (!response.ok) {
                throw new Error('Failed to fetch pooja tags');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const createPoojaTag = createAsyncThunk(
    'poojaTag/create',
    async (payload, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/tag`, {
                method: "POST",
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            if (!response.ok) {
                throw new Error('Failed to create pooja tag');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updatePoojaTag = createAsyncThunk(
    'poojaTag/update',
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/tag/${id}`, {
                method: "PATCH", // Usually PATCH or PUT
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            if (!response.ok) {
                throw new Error('Failed to update pooja tag');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const deletePoojaTag = createAsyncThunk(
    'poojaTag/delete',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/tag/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            if (!response.ok) {
                throw new Error('Failed to delete pooja tag');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Slice
const poojaTagSlice = createSlice({
    name: 'poojaTag',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchPoojaTag.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
            })
            .addCase(fetchPoojaTag.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                state.poojaTag = action.payload?.data || [];
                state.pagination = action.payload?.paginationDetail || initialState.pagination;
            })
            .addCase(fetchPoojaTag.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload || action.error.message;
            });
    },
});

export default poojaTagSlice.reducer;
